import { Popover } from '@mindpal-co/mindpal-ui';
import { useEffect, useState } from 'react';

import Button from '@/components/Button';
import Icon from '@/components/Icon';
import Text from '@/components/Text';
import { PopoverContent } from '@/components/ui/popover';
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { useJobBoardParams } from '@/hooks/JobBoardFilters/useJobBoardParams';

import { FilterTrigger } from './FilterTrigger';
import type { IndustryFormValues } from './Industries';
import Industries, { INDUSTRY_FORM_ID } from './Industries';
import JobTitle, { JOB_TITLE_FORM_ID } from './JobTitle';
import Location from './Location';
import type { SalaryParams } from './Salary';
import Salary, { SALARY_FORM_ID } from './Salary';

const Filters = () => {
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [mobileFilter, setMobileFilter] = useState<string | null>(null);

  const { params, isReady } = useJobBoardParams();

  const industryDefaults: IndustryFormValues = {
    industry: params.industry ?? '',
    seniorityLevel: params.seniorityLevel ?? [],
  };

  const salaryDefaults: SalaryParams = {
    salaryLower: params.salaryLower,
    salaryUpper: params.salaryUpper,
  };

  const sections = [
    {
      title: 'Industry',
      emptyValue: 'Choose industries',
      summary: [industryDefaults.industry, ...industryDefaults.seniorityLevel]
        .filter(Boolean)
        .join(', '),
      render: () => <Industries defaultValues={industryDefaults} />,
      formId: INDUSTRY_FORM_ID,
    },
    {
      title: 'Salary',
      emptyValue: 'Choose salary',
      summary:
        salaryDefaults.salaryLower === undefined &&
        salaryDefaults.salaryUpper === undefined
          ? ''
          : [salaryDefaults.salaryLower, salaryDefaults.salaryUpper]
              .map((bound) =>
                bound === undefined ? 'any' : `$${bound / 1000}K`
              )
              .join(' - '),
      render: () => <Salary defaultValues={salaryDefaults} />,
      formId: SALARY_FORM_ID,
    },
  ];

  const selectedMobileFilter = sections.find(
    ({ formId }) => formId === mobileFilter
  );

  useEffect(() => {
    if (!isReady) return;
    setMobileFilter(null);
  }, [JSON.stringify(params)]);

  return (
    <>
      <div className="flex lg:hidden">
        <Sheet
          open={isSheetOpen}
          onOpenChange={(open) => {
            setIsSheetOpen(open);
            if (!open) setMobileFilter(null);
          }}
        >
          <SheetTrigger asChild>
            <Button
              variant="candidate-quaternary"
              size="candidate-sm"
              className="w-full"
              prefixIcon="material-symbols-light:tune"
            >
              Filters
            </Button>
          </SheetTrigger>
          <SheetContent
            side="right"
            aria-describedby={undefined}
            className="flex w-11/12 !max-w-none flex-col gap-0 overflow-y-auto p-0"
          >
            <div className="flex items-center py-4 pl-5 pr-12">
              <div className="flex items-center gap-1">
                {selectedMobileFilter && (
                  <Button
                    variant="unstyled"
                    size="xs"
                    aria-label="Back to all filters"
                    onClick={() => setMobileFilter(null)}
                  >
                    <Icon
                      icon="material-symbols-light:arrow-back"
                      className="size-5 text-jb-neutral-1000"
                    />
                    <SheetTitle className="!text-jb-body-large !font-semibold text-jb-neutral-1000">
                      Filter
                    </SheetTitle>
                  </Button>
                )}
                {!selectedMobileFilter && (
                  <SheetTitle className="!text-jb-body-large !font-semibold text-jb-neutral-1000">
                    Filter
                  </SheetTitle>
                )}
              </div>
            </div>
            {!selectedMobileFilter && (
              <div className="flex flex-col gap-3 px-5 pb-5 pt-2">
                <div className="flex flex-col divide-y border-y [&>*]:py-3">
                  <JobTitle />
                  <Location />
                  {sections.map(({ emptyValue, formId, summary, title }) => (
                    <button
                      key={formId}
                      type="button"
                      onClick={() => setMobileFilter(formId)}
                      className="flex items-center justify-between gap-2 pr-3 text-left"
                    >
                      <span className="flex flex-col">
                        <Text
                          variant="jb-body-large"
                          className="font-semibold text-jb-neutral-1000"
                        >
                          {title}
                        </Text>
                        <Text
                          variant="jb-body-small"
                          className="font-medium text-jb-neutral-900"
                        >
                          {summary || emptyValue}
                        </Text>
                      </span>
                      <Icon
                        icon="material-symbols-light:keyboard-arrow-down"
                        className="size-4 shrink-0 text-jb-neutral-1000"
                      />
                    </button>
                  ))}
                </div>
                <Button
                  type="submit"
                  form={JOB_TITLE_FORM_ID}
                  variant="candidate-black"
                  size="candidate-md"
                  className="w-full"
                >
                  Search
                </Button>
              </div>
            )}
            {selectedMobileFilter && (
              <div className="flex flex-col border-t p-5">
                {selectedMobileFilter?.render()}
              </div>
            )}
          </SheetContent>
        </Sheet>
      </div>
      <div className="hidden w-full flex-row gap-2 lg:flex">
        <div className="flex w-full flex-row items-center divide-x-2 rounded-xl border border-jb-neutral-600 p-1 [&>*:first-child]:pl-2 [&>*:last-child]:pr-2 [&>*]:px-6">
          <JobTitle />
          <Location />
          {sections.map(({ emptyValue, formId, render, summary, title }) => (
            <Popover key={formId}>
              <FilterTrigger title={title}>
                {summary ? (
                  <span className="text-jb-primary-400">{summary}</span>
                ) : (
                  emptyValue
                )}
              </FilterTrigger>
              <PopoverContent
                variant="jb-primary"
                align="start"
                className="flex !w-[48rem] max-w-[calc(100vw-2rem)] flex-col gap-6 bg-jb-neutral-100"
              >
                {render()}
              </PopoverContent>
            </Popover>
          ))}
        </div>
        <Button
          type="submit"
          form={JOB_TITLE_FORM_ID}
          variant="candidate-black"
          className="flex-1 !py-0"
          size="candidate-md"
        >
          Search
        </Button>
      </div>
    </>
  );
};

export default Filters;
